import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges,
  OnDestroy, OnInit, Output, SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VesselCardService } from '../../services/vessel-card.service';
import {
  CardIdentity, CardParams, CardParticipant, CardUnits, CardView, ChatArgs, QuickObs,
  avatarInitials, buildCardView,
} from '../../shared/utils/vessel-card.logic';

export interface VesselCardProfileRequest {
  userId: string;
  name: string;
  imageUrl: string | null;
  isSelf: boolean;
}

export interface VesselCardParticipantsRequest {
  participants: CardParticipant[];
  vesselName: string | null;
  tripId: string | null;
}

/**
 * Boat card — a port of Flutter's `_CompactObservationPopup`
 * (`home_screen_observation_popup.dart`): navy header with the vessel name, speed/course
 * headline, water temp + depth, position, ride-along crew row, then the owner (tap →
 * profile) and a chat button. The Water Drop button is mobile-only and deliberately absent.
 *
 * View only. Extraction, owner/chat rules and chat routing live in `vessel-card.logic.ts`;
 * network access in `VesselCardService`.
 */
@Component({
  selector: 'app-vessel-card',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './vessel-card.component.html',
  styleUrl: './vessel-card.component.scss',
})
export class VesselCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) params!: CardParams;
  /** A reading the host already fetched (skips the network load). */
  @Input() prefetched: any = null;
  @Input() identity: CardIdentity = { ids: [], names: [] };
  @Input() units: CardUnits = {};
  /** Chat is a product-level toggle on web (see `CHAT_UI_ENABLED`). */
  @Input() chatEnabled = false;

  @Output() closed = new EventEmitter<void>();
  @Output() participantsRequested = new EventEmitter<VesselCardParticipantsRequest>();
  @Output() profileRequested = new EventEmitter<VesselCardProfileRequest>();
  @Output() chatRequested = new EventEmitter<ChatArgs>();

  @HostBinding('class.vessel-card-host') readonly hostClass = true;

  isLoading = true;
  hasError = false;
  quick: QuickObs | null = null;
  view: CardView | null = null;

  /** Owner shown in the identity row after the profile lookup settled. */
  ownerDisplayName: string | null = null;
  ownerDisplayImage: string | null = null;

  static readonly AVATAR_OVERLAP_PX = 16;
  static readonly AVATAR_SIZE_PX = 24;
  static readonly MAX_VISIBLE_AVATARS = 3;

  private destroyed = false;
  private loadSeq = 0;

  constructor(private data: VesselCardService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Identity/units can land after the card opens (profile still loading): re-derive, don't refetch.
    if (this.quick && (changes['identity'] || changes['units'] || changes['chatEnabled']) && !changes['params']?.firstChange) {
      this.rebuild();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  get headerName(): string {
    return this.params.vesselName?.trim() || this.quick?.vessel_name?.trim() || '--';
  }

  get visibleParticipants(): CardParticipant[] {
    return (this.view?.participants ?? []).slice(0, VesselCardComponent.MAX_VISIBLE_AVATARS);
  }

  get overflowCount(): number {
    return Math.max(0, (this.view?.participants.length ?? 0) - VesselCardComponent.MAX_VISIBLE_AVATARS);
  }

  /** Width of the stacked-avatar strip: up to three avatars plus the "+N" chip. */
  get avatarStripWidth(): number {
    const count = this.view?.participants.length ?? 0;
    const visible = count > 3 ? 4 : count;
    return visible <= 0
      ? 0
      : VesselCardComponent.AVATAR_SIZE_PX + (visible - 1) * VesselCardComponent.AVATAR_OVERLAP_PX;
  }

  /** Own boat opens your profile, anyone else's opens theirs; the only dead case is an unidentifiable owner. */
  get canOpenProfile(): boolean {
    return !!this.view && (this.view.isMyBoat || !!this.view.ownerUserId);
  }

  initials(name?: string | null): string {
    return avatarInitials(name);
  }

  titleCase(name?: string | null): string {
    return (name ?? '').trim().replace(/\S+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }

  onClose(): void {
    this.closed.emit();
  }

  onParticipantsClick(): void {
    if (!this.view) return;
    this.participantsRequested.emit({
      participants: this.view.participants,
      vesselName: this.quick?.vessel_name ?? this.params.vesselName ?? null,
      tripId: this.view.chatArgs.tripId,
    });
  }

  onOwnerClick(): void {
    if (!this.view || !this.canOpenProfile) return;
    // Self is decided by ID whenever there is one: the ownership heuristic also matches on
    // display NAME, and a name collision must not hijack another captain's profile.
    const userId = this.view.ownerUserId ?? '';
    const isSelf = userId ? this.identity.ids.includes(userId) : this.view.isMyBoat;
    this.profileRequested.emit({
      userId,
      name: this.ownerDisplayName ?? this.view.ownerName ?? '',
      imageUrl: this.ownerDisplayImage,
      isSelf,
    });
  }

  onChatClick(): void {
    if (this.view) this.chatRequested.emit(this.view.chatArgs);
  }

  // ── Loading ─────────────────────────────────────────────────────────────────

  private async load(): Promise<void> {
    const seq = ++this.loadSeq;
    try {
      const obs = this.prefetched ?? (await this.fetchObservation());
      if (this.destroyed || seq !== this.loadSeq) return;
      this.quick = this.data.extract(obs);
      this.isLoading = false;
      this.hasError = false;
      this.rebuild();
    } catch {
      if (this.destroyed || seq !== this.loadSeq) return;
      this.isLoading = false;
      // A card that already has a position to show beats an error.
      if (this.params.fallbackLat != null && this.params.fallbackLon != null) {
        this.quick = this.data.extract({});
        this.rebuild();
      } else {
        this.hasError = true;
        this.cdr.markForCheck();
      }
      return;
    }

    // Ride-along context arrives in the background (asynchronous enrichment).
    const targetVesselId = this.quick?.vessel_ref_id?.trim() || this.params.vesselId?.trim();
    if (!targetVesselId) return;
    const ctx = await this.data.fetchRideAlongContext(targetVesselId);
    if (!ctx || this.destroyed || seq !== this.loadSeq || !this.quick) return;
    this.quick = {
      ...this.quick,
      ride_along_participants: ctx.participants,
      ride_along_enabled: ctx.enabled,
      trip_state: ctx.tripState,
      ride_along_trip_name: ctx.tripName ?? this.quick.ride_along_trip_name,
      ride_along_trip_id: ctx.tripId,
      vessel_name: this.quick.vessel_name?.trim() ? this.quick.vessel_name : this.captainVesselName(ctx.participants),
    };
    this.rebuild();
  }

  private fetchObservation(): Promise<any> {
    const vesselId = this.params.vesselId?.trim();
    const obsId = this.params.observationId?.trim();
    // A specific observation beats the vessel's "latest" bucket.
    if (obsId && obsId !== 'live') return this.data.loadById(obsId);
    if (vesselId) return this.data.loadLatest(vesselId);
    return Promise.reject(new Error('nothing to load'));
  }

  private captainVesselName(participants: CardParticipant[]): string | null {
    const c = participants.find((p) => p.role?.trim().toLowerCase() === 'captain');
    return c?.vesselName?.trim() || null;
  }

  private rebuild(): void {
    if (!this.quick) return;
    this.view = buildCardView(this.quick, this.params, this.identity, this.units, this.chatEnabled);
    this.ownerDisplayName = this.view.ownerName;
    this.ownerDisplayImage = this.view.ownerImageUrl;
    this.cdr.markForCheck();
    void this.resolveOwnerIdentity(this.view);
  }

  /** The face must belong to the person the row NAMES; a mismatching lookup drops its photo. */
  private async resolveOwnerIdentity(view: CardView): Promise<void> {
    const userId = view.ownerUserId?.trim() ?? '';
    const seedName = view.ownerName?.trim() ?? '';
    const seedImage = view.ownerImageUrl?.trim() ?? '';
    if (!userId || (seedName && seedImage)) return;

    const found = await this.data.lookupOwnerIdentity(userId);
    if (this.destroyed || this.view !== view) return;
    const lookedUp = found.name?.trim() ?? '';
    const samePerson = !seedName || !lookedUp || lookedUp.toLowerCase() === seedName.toLowerCase();
    this.ownerDisplayName = seedName || lookedUp || null;
    this.ownerDisplayImage = seedImage || (samePerson ? found.imageUrl : null);
    this.cdr.markForCheck();
  }
}
